import React, { useState, useRef } from 'react';
import { MailIcon, InstagramIcon, LinkedinIcon, SendIcon, AlertCircleIcon, CheckCircleIcon, LoaderIcon, UserIcon, AtSignIcon, MessageSquareIcon, TagIcon, GlobeIcon, CalendarIcon, ZapIcon, TwitterIcon, GithubIcon, YoutubeIcon, SlackIcon } from 'lucide-react';
export const Contact = ({
  onCursorChange
}) => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    location: '',
    project: '',
    availability: '',
    subscribe: true,
    interest: 'collaboration'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [systemStatus, setSystemStatus] = useState({
    connection: 'SECURE',
    encryption: 'ENABLED',
    bandwidth: 'OPTIMAL',
    latency: '12ms'
  });
  const formRef = useRef(null);
  const handleInputChange = e => {
    const {
      id,
      value,
      type,
      checked
    } = e.target;
    setFormState(prev => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = e => {
    e.preventDefault();
    setIsSubmitting(true);
    // Update system status during submission
    setSystemStatus({
      connection: 'PROCESSING',
      encryption: 'ACTIVE',
      bandwidth: 'ALLOCATING',
      latency: '8ms'
    });
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      // Update system status after submission
      setSystemStatus({
        connection: 'ESTABLISHED',
        encryption: 'VERIFIED',
        bandwidth: 'OPTIMAL',
        latency: '5ms'
      });
      // Reset form after showing success message
      setTimeout(() => {
        setIsSubmitted(false);
        setFormState({
          name: '',
          email: '',
          subject: '',
          message: '',
          location: '',
          project: '',
          availability: '',
          subscribe: true,
          interest: 'collaboration'
        });
        // Reset system status
        setSystemStatus({
          connection: 'SECURE',
          encryption: 'ENABLED',
          bandwidth: 'OPTIMAL',
          latency: '12ms'
        });
      }, 5000);
    }, 2000);
  };
  // Animated button component
  const AnimatedButton = ({
    children,
    primary = false,
    href = null,
    onClick = null,
    disabled = false
  }) => {
    return href ? <a href={href} className={`
          flex items-center justify-center gap-2 px-6 py-3 rounded-lg
          ${primary ? 'glass text-[#5ee7df] border border-[#5ee7df]/50 hover:bg-[#5ee7df]/10' : 'glass border border-[#8a9cb9]/30 text-[#8a9cb9] hover:bg-[#8a9cb9]/10'}
          transition-all duration-300 font-mono relative overflow-hidden group
        `} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-370">
        <span className="relative z-10 flex items-center gap-2" data-id="element-371">
          {children}
        </span>
        <span className="absolute bottom-0 left-0 h-px w-0 bg-current group-hover:w-full transition-all duration-300" data-id="element-372"></span>
        {/* Interface corners */}
        <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#5ee7df]/50 opacity-0 group-hover:opacity-100 transition-all duration-300" data-id="element-373"></span>
        <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#5ee7df]/50 opacity-0 group-hover:opacity-100 transition-all duration-300" data-id="element-374"></span>
        <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#5ee7df]/50 opacity-0 group-hover:opacity-100 transition-all duration-300" data-id="element-375"></span>
        <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#5ee7df]/50 opacity-0 group-hover:opacity-100 transition-all duration-300" data-id="element-376"></span>
      </a> : <button onClick={onClick} disabled={disabled} className={`
          flex items-center justify-center gap-2 px-6 py-3 rounded-lg
          ${primary ? 'glass text-[#5ee7df] border border-[#5ee7df]/50 hover:bg-[#5ee7df]/10' : 'glass border border-[#8a9cb9]/30 text-[#8a9cb9] hover:bg-[#8a9cb9]/10'}
          transition-all duration-300 font-mono relative overflow-hidden group w-full
          ${disabled ? 'opacity-70 cursor-not-allowed' : ''}
        `} onMouseEnter={() => !disabled && onCursorChange('button')} onMouseLeave={() => !disabled && onCursorChange('default')} data-id="element-377">
        <span className="relative z-10 flex items-center gap-2" data-id="element-378">
          {children}
        </span>
        <span className={`absolute bottom-0 left-0 h-px w-0 bg-current ${!disabled ? 'group-hover:w-full' : ''} transition-all duration-300`} data-id="element-379"></span>
        {/* Interface corners */}
        <span className={`absolute top-0 left-0 w-3 h-3 border-t border-l border-[#5ee7df]/50 opacity-0 ${!disabled ? 'group-hover:opacity-100' : ''} transition-all duration-300`} data-id="element-380"></span>
        <span className={`absolute top-0 right-0 w-3 h-3 border-t border-r border-[#5ee7df]/50 opacity-0 ${!disabled ? 'group-hover:opacity-100' : ''} transition-all duration-300`} data-id="element-381"></span>
        <span className={`absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#5ee7df]/50 opacity-0 ${!disabled ? 'group-hover:opacity-100' : ''} transition-all duration-300`} data-id="element-382"></span>
        <span className={`absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#5ee7df]/50 opacity-0 ${!disabled ? 'group-hover:opacity-100' : ''} transition-all duration-300`} data-id="element-383"></span>
      </button>;
  };
  // Social media links that would typically be on a Linktree
  const socialLinks = [{
    icon: <InstagramIcon size={20} data-id="element-384" />,
    label: 'Instagram',
    url: 'https://instagram.com/cherylqian'
  }, {
    icon: <TwitterIcon size={20} data-id="element-385" />,
    label: 'Twitter',
    url: 'https://twitter.com/cherylqian'
  }, {
    icon: <LinkedinIcon size={20} data-id="element-386" />,
    label: 'LinkedIn',
    url: 'https://linkedin.com/in/cherylqian'
  }, {
    icon: <SlackIcon size={20} data-id="element-387" />,
    label: 'Slack',
    url: 'https://yacht-labs.slack.com'
  }, {
    icon: <GithubIcon size={20} data-id="element-388" />,
    label: 'GitHub',
    url: 'https://github.com/cherylqian'
  }, {
    icon: <YoutubeIcon size={20} data-id="element-389" />,
    label: 'YouTube',
    url: 'https://youtube.com/@cherylqian'
  }, {
    icon: <MailIcon size={20} data-id="element-390" />,
    label: 'Email',
    url: 'mailto:contact@cherylqian.com'
  }];
  return <section id="contact" className="w-full py-24 bg-[#060a10] relative" data-id="element-391">
      <style jsx global data-id="element-392">{`
        .tech-grid {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image:
            linear-gradient(
              to right,
              rgba(94, 231, 223, 0.05) 1px,
              transparent 1px
            ),
            linear-gradient(
              to bottom,
              rgba(94, 231, 223, 0.05) 1px,
              transparent 1px
            );
          background-size: 20px 20px;
          opacity: 0.2;
          pointer-events: none;
        }
        .contact-input {
          transition: all 0.3s ease;
          position: relative;
        }
        .contact-input:focus-within {
          transform: translateY(-2px);
        }
        .contact-input::before,
        .contact-input::after {
          content: '';
          position: absolute;
          width: 5px;
          height: 5px;
          opacity: 0;
          transition: all 0.3s ease;
        }
        .contact-input::before {
          top: 0;
          left: 0;
          border-top: 1px solid #5ee7df;
          border-left: 1px solid #5ee7df;
        }
        .contact-input::after {
          bottom: 0;
          right: 0;
          border-bottom: 1px solid #5ee7df;
          border-right: 1px solid #5ee7df;
        }
        .contact-input:focus-within::before,
        .contact-input:focus-within::after {
          width: 10px;
          height: 10px;
          opacity: 1;
        }
        .system-status-item {
          display: flex;
          justify-content: space-between;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.7rem;
          color: #8a9cb9;
          padding: 4px 0;
          border-bottom: 1px solid rgba(26, 42, 64, 0.5);
        }
        .system-status-item:last-child {
          border-bottom: none;
        }
        .system-status-value {
          color: #5ee7df;
        }
        .scan-line {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 100%;
          background: linear-gradient(
            to bottom,
            transparent,
            rgba(94, 231, 223, 0.1),
            transparent
          );
          opacity: 0;
          animation: scan-animation 3s linear infinite;
          pointer-events: none;
        }
        @keyframes scan-animation {
          0% {
            top: -100%;
            opacity: 0.3;
          }
          100% {
            top: 100%;
            opacity: 0.3;
          }
        }
        .success-icon {
          animation: success-pulse 2s infinite;
        }
        @keyframes success-pulse {
          0%,
          100% {
            opacity: 0.7;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.1);
          }
        }
        /* Custom radio buttons */
        .radio-container {
          display: flex;
          gap: 1rem;
          margin-top: 0.5rem;
        }
        .radio-item {
          position: relative;
          flex: 1;
        }
        .radio-item input {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
        }
        .radio-item label {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 1rem 0.5rem;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          color: #8a9cb9;
          background: rgba(10, 16, 24, 0.5);
          border: 1px solid rgba(26, 42, 64, 0.5);
          border-radius: 0.25rem;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
        }
        .radio-item input:checked + label {
          color: #5ee7df;
          border-color: #5ee7df;
          background: rgba(94, 231, 223, 0.1);
        }
        .radio-item label:hover {
          border-color: rgba(94, 231, 223, 0.5);
        }
        .radio-item input:checked + label::before {
          content: '';
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          border: 1px solid #5ee7df;
          border-radius: 0.25rem;
          opacity: 0.5;
          pointer-events: none;
        }
        .radio-icon {
          margin-bottom: 0.5rem;
        }
        /* Custom checkbox */
        .checkbox-container {
          position: relative;
          display: flex;
          align-items: center;
          margin-top: 1rem;
        }
        .checkbox-container input {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
        }
        .checkbox-container label {
          display: flex;
          align-items: center;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          color: #8a9cb9;
          cursor: pointer;
        }
        .checkbox-container .checkmark {
          position: relative;
          display: inline-block;
          width: 18px;
          height: 18px;
          margin-right: 0.5rem;
          background: rgba(10, 16, 24, 0.5);
          border: 1px solid rgba(26, 42, 64, 0.5);
          border-radius: 0.25rem;
          transition: all 0.3s ease;
        }
        .checkbox-container input:checked + label .checkmark {
          background: rgba(94, 231, 223, 0.2);
          border-color: #5ee7df;
        }
        .checkbox-container .checkmark::after {
          content: '';
          position: absolute;
          top: 3px;
          left: 6px;
          width: 5px;
          height: 9px;
          border: solid #5ee7df;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        .checkbox-container input:checked + label .checkmark::after {
          opacity: 1;
        }
        .social-links-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
          gap: 12px;
          margin-top: 20px;
        }
        .social-link {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 12px 8px;
          background: rgba(10, 16, 24, 0.5);
          border: 1px solid rgba(26, 42, 64, 0.5);
          border-radius: 8px;
          transition: all 0.3s ease;
        }
        .social-link:hover {
          border-color: #5ee7df;
          background: rgba(94, 231, 223, 0.1);
          transform: translateY(-3px);
        }
        .social-icon {
          margin-bottom: 8px;
          color: #8a9cb9;
          transition: color 0.3s ease;
        }
        .social-link:hover .social-icon {
          color: #5ee7df;
        }
        .social-label {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.7rem;
          color: #8a9cb9;
          transition: color 0.3s ease;
        }
        .social-link:hover .social-label {
          color: #5ee7df;
        }
      `}</style>
      {/* Background effects - darkened */}
      <div className="absolute inset-0 bg-[#030508] opacity-80" data-id="element-393"></div>
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-30 mix-blend-overlay" data-id="element-394"></div>
      <div className="absolute inset-0 bg-[url('https://upload.wikimedia.org/wikipedia/commons/thumb/2/29/Haeckel_Siphonophorae.jpg/800px-Haeckel_Siphonophorae.jpg')] bg-center bg-no-repeat bg-cover opacity-3 mix-blend-color-burn" data-id="element-395"></div>
      <div className="tech-grid" data-id="element-396"></div>
      <div className="container mx-auto px-6 relative z-10" data-id="element-397">
        <div className="max-w-3xl mx-auto text-center" data-id="element-398">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-gradient font-heading animate-fade-in-up" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-399">
            CONNECT
          </h2>
          <p className="text-[#8a9cb9] mb-8 font-mono animate-fade-in-up" style={{
          animationDelay: '0.2s'
        }} onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-400">
            Host my experiments at the flesh-tech intersection.
          </p>
          {/* Social Links Grid */}
          <div className="social-links-grid mb-10 animate-fade-in-up" style={{
          animationDelay: '0.3s'
        }} data-id="element-401">
            {socialLinks.map((link, index) => <a key={index} href={link.url} target="_blank" rel="noopener noreferrer" className="social-link" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-402">
                <div className="social-icon" data-id="element-403">{link.icon}</div>
                <div className="social-label" data-id="element-404">{link.label}</div>
              </a>)}
          </div>
          <div className="flex flex-col md:flex-row justify-center gap-4 mb-10 animate-fade-in-up" style={{
          animationDelay: '0.4s'
        }} data-id="element-405">
            <AnimatedButton primary href="mailto:contact@cherylqian.com" data-id="element-406">
              <MailIcon size={18} data-id="element-407" />
              <span data-id="element-408">TRANSMIT</span>
            </AnimatedButton>
            <AnimatedButton href="https://instagram.com/cherylqian" data-id="element-409">
              <InstagramIcon size={18} data-id="element-410" />
              <span data-id="element-411">OBSERVE</span>
            </AnimatedButton>
            <AnimatedButton href="https://linkedin.com/in/cherylqian" data-id="element-412">
              <LinkedinIcon size={18} data-id="element-413" />
              <span data-id="element-414">NETWORK</span>
            </AnimatedButton>
          </div>
          <div className="glass p-8 shadow-lg border border-[#1a2a40]/50 rounded-lg animate-fade-in-up relative" style={{
          animationDelay: '0.6s'
        }} ref={formRef} data-id="element-415">
            {/* Scan line effect */}
            <div className="scan-line" data-id="element-416"></div>
            <div className="flex justify-between items-start mb-6" data-id="element-417">
              <h3 className="text-xl font-bold text-[#e0e6f0] font-heading" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-418">
                SUBMIT DATA
              </h3>
              {/* System status */}
              <div className="bg-[#0a1018] p-3 rounded border border-[#1a2a40]/50 text-left w-48" data-id="element-419">
                <div className="text-xs text-[#5ee7df] mb-2 font-mono" data-id="element-420">
                  SYSTEM STATUS
                </div>
                <div className="system-status-item" data-id="element-421">
                  <span data-id="element-422">CONNECTION:</span>
                  <span className="system-status-value" data-id="element-423">
                    {systemStatus.connection}
                  </span>
                </div>
                <div className="system-status-item" data-id="element-424">
                  <span data-id="element-425">ENCRYPTION:</span>
                  <span className="system-status-value" data-id="element-426">
                    {systemStatus.encryption}
                  </span>
                </div>
                <div className="system-status-item" data-id="element-427">
                  <span data-id="element-428">BANDWIDTH:</span>
                  <span className="system-status-value" data-id="element-429">
                    {systemStatus.bandwidth}
                  </span>
                </div>
                <div className="system-status-item" data-id="element-430">
                  <span data-id="element-431">LATENCY:</span>
                  <span className="system-status-value" data-id="element-432">
                    {systemStatus.latency}
                  </span>
                </div>
              </div>
            </div>
            {isSubmitted ? <div className="py-8 text-center" data-id="element-433">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full glass border border-[#5ee7df]/50 mb-4 text-[#5ee7df]" data-id="element-434">
                  <CheckCircleIcon size={24} className="success-icon" data-id="element-435" />
                </div>
                <p className="text-[#5ee7df] font-mono text-lg" data-id="element-436">
                  TRANSMISSION SUCCESSFUL
                </p>
                <p className="text-[#8a9cb9] mt-2 font-mono" data-id="element-437">
                  Data processed and securely transmitted.
                </p>
                <div className="mt-4 p-3 bg-[#0a1018]/50 rounded border border-[#1a2a40]/50 inline-block" data-id="element-438">
                  <div className="text-xs text-[#5ee7df] font-mono flex items-center" data-id="element-439">
                    <span className="inline-block w-2 h-2 rounded-full bg-[#5ee7df] animate-pulse mr-2" data-id="element-440"></span>
                    CONNECTION ESTABLISHED
                  </div>
                </div>
              </div> : <form onSubmit={handleSubmit} data-id="element-441">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-442">
                  <div className="relative contact-input" data-id="element-443">
                    <label htmlFor="name" className="block text-sm font-medium text-[#8a9cb9] mb-2 font-mono flex items-center" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-444">
                      <UserIcon size={14} className="mr-2" data-id="element-445" />
                      NAME
                    </label>
                    <input type="text" id="name" value={formState.name} onChange={handleInputChange} className="w-full px-4 py-2 glass border border-[#1a2a40]/50 focus:border-[#5ee7df]/50 text-[#e0e6f0] focus:outline-none focus:ring-1 focus:ring-[#5ee7df] font-mono transition-all duration-300 rounded-lg" required disabled={isSubmitting} onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-446" />
                  </div>
                  <div className="relative contact-input" data-id="element-447">
                    <label htmlFor="email" className="block text-sm font-medium text-[#8a9cb9] mb-2 font-mono flex items-center" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-448">
                      <AtSignIcon size={14} className="mr-2" data-id="element-449" />
                      EMAIL
                    </label>
                    <input type="email" id="email" value={formState.email} onChange={handleInputChange} className="w-full px-4 py-2 glass border border-[#1a2a40]/50 focus:border-[#5ee7df]/50 text-[#e0e6f0] focus:outline-none focus:ring-1 focus:ring-[#5ee7df] font-mono transition-all duration-300 rounded-lg" required disabled={isSubmitting} onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-450" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-451">
                  <div className="relative contact-input" data-id="element-452">
                    <label htmlFor="subject" className="block text-sm font-medium text-[#8a9cb9] mb-2 font-mono flex items-center" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-453">
                      <TagIcon size={14} className="mr-2" data-id="element-454" />
                      SUBJECT
                    </label>
                    <input type="text" id="subject" value={formState.subject} onChange={handleInputChange} className="w-full px-4 py-2 glass border border-[#1a2a40]/50 focus:border-[#5ee7df]/50 text-[#e0e6f0] focus:outline-none focus:ring-1 focus:ring-[#5ee7df] font-mono transition-all duration-300 rounded-lg" disabled={isSubmitting} onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-455" />
                  </div>
                  <div className="relative contact-input" data-id="element-456">
                    <label htmlFor="location" className="block text-sm font-medium text-[#8a9cb9] mb-2 font-mono flex items-center" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-457">
                      <GlobeIcon size={14} className="mr-2" data-id="element-458" />
                      LOCATION
                    </label>
                    <input type="text" id="location" value={formState.location} onChange={handleInputChange} className="w-full px-4 py-2 glass border border-[#1a2a40]/50 focus:border-[#5ee7df]/50 text-[#e0e6f0] focus:outline-none focus:ring-1 focus:ring-[#5ee7df] font-mono transition-all duration-300 rounded-lg" disabled={isSubmitting} onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} placeholder="CITY, COUNTRY" data-id="element-459" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-460">
                  <div className="relative contact-input" data-id="element-461">
                    <label htmlFor="project" className="block text-sm font-medium text-[#8a9cb9] mb-2 font-mono flex items-center" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-462">
                      <MessageSquareIcon size={14} className="mr-2" data-id="element-463" />
                      PROJECT TYPE
                    </label>
                    <select id="project" value={formState.project} onChange={handleInputChange} className="w-full px-4 py-2 glass border border-[#1a2a40]/50 focus:border-[#5ee7df]/50 text-[#e0e6f0] focus:outline-none focus:ring-1 focus:ring-[#5ee7df] font-mono transition-all duration-300 rounded-lg bg-transparent" disabled={isSubmitting} onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-464">
                      <option value="" className="bg-[#0a1018]" data-id="element-465">
                        SELECT PROJECT TYPE
                      </option>
                      <option value="installation" className="bg-[#0a1018]" data-id="element-466">
                        BIO INSTALLATION
                      </option>
                      <option value="digital" className="bg-[#0a1018]" data-id="element-467">
                        DIGITAL INFECTION
                      </option>
                      <option value="exhibition" className="bg-[#0a1018]" data-id="element-468">
                        EXHIBITION
                      </option>
                      <option value="collaboration" className="bg-[#0a1018]" data-id="element-469">
                        COLLABORATION
                      </option>
                      <option value="other" className="bg-[#0a1018]" data-id="element-470">
                        OTHER
                      </option>
                    </select>
                  </div>
                  <div className="relative contact-input" data-id="element-471">
                    <label htmlFor="availability" className="block text-sm font-medium text-[#8a9cb9] mb-2 font-mono flex items-center" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-472">
                      <CalendarIcon size={14} className="mr-2" data-id="element-473" />
                      TIMEFRAME
                    </label>
                    <input type="text" id="availability" value={formState.availability} onChange={handleInputChange} className="w-full px-4 py-2 glass border border-[#1a2a40]/50 focus:border-[#5ee7df]/50 text-[#e0e6f0] focus:outline-none focus:ring-1 focus:ring-[#5ee7df] font-mono transition-all duration-300 rounded-lg" disabled={isSubmitting} onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} placeholder="WHEN NEEDED?" data-id="element-474" />
                  </div>
                </div>
                <div className="mb-6 relative contact-input" data-id="element-475">
                  <label htmlFor="message" className="block text-sm font-medium text-[#8a9cb9] mb-2 font-mono flex items-center" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-476">
                    <MessageSquareIcon size={14} className="mr-2" data-id="element-477" />
                    MESSAGE
                  </label>
                  <textarea id="message" rows={4} value={formState.message} onChange={handleInputChange} className="w-full px-4 py-2 glass border border-[#1a2a40]/50 focus:border-[#5ee7df]/50 text-[#e0e6f0] focus:outline-none focus:ring-1 focus:ring-[#5ee7df] font-mono transition-all duration-300 rounded-lg" required disabled={isSubmitting} onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-478"></textarea>
                </div>
                {/* Interest type selector */}
                <div className="mb-6" data-id="element-479">
                  <label className="block text-sm font-medium text-[#8a9cb9] mb-2 font-mono" data-id="element-480">
                    INTEREST
                  </label>
                  <div className="radio-container" data-id="element-481">
                    <div className="radio-item" data-id="element-482">
                      <input type="radio" id="collaboration" name="interest" value="collaboration" checked={formState.interest === 'collaboration'} onChange={handleInputChange} disabled={isSubmitting} data-id="element-483" />
                      <label htmlFor="collaboration" data-id="element-484">
                        <span className="radio-icon" data-id="element-485">
                          <UserIcon size={18} data-id="element-486" />
                        </span>
                        COLLAB
                      </label>
                    </div>
                    <div className="radio-item" data-id="element-487">
                      <input type="radio" id="commission" name="interest" value="commission" checked={formState.interest === 'commission'} onChange={handleInputChange} disabled={isSubmitting} data-id="element-488" />
                      <label htmlFor="commission" data-id="element-489">
                        <span className="radio-icon" data-id="element-490">
                          <ZapIcon size={18} data-id="element-491" />
                        </span>
                        COMMISSION
                      </label>
                    </div>
                    <div className="radio-item" data-id="element-492">
                      <input type="radio" id="inquiry" name="interest" value="inquiry" checked={formState.interest === 'inquiry'} onChange={handleInputChange} disabled={isSubmitting} data-id="element-493" />
                      <label htmlFor="inquiry" data-id="element-494">
                        <span className="radio-icon" data-id="element-495">
                          <MessageSquareIcon size={18} data-id="element-496" />
                        </span>
                        INQUIRY
                      </label>
                    </div>
                  </div>
                </div>
                {/* Subscribe checkbox */}
                <div className="checkbox-container mb-6" data-id="element-497">
                  <input type="checkbox" id="subscribe" checked={formState.subscribe} onChange={handleInputChange} disabled={isSubmitting} data-id="element-498" />
                  <label htmlFor="subscribe" data-id="element-499">
                    <span className="checkmark" data-id="element-500"></span>
                    SUBSCRIBE TO UPDATES
                  </label>
                </div>
                <AnimatedButton primary onClick={handleSubmit} disabled={isSubmitting} data-id="element-501">
                  {isSubmitting ? <>
                      <LoaderIcon size={18} className="animate-spin" data-id="element-502" />
                      <span data-id="element-503">PROCESSING...</span>
                    </> : <>
                      <SendIcon size={18} data-id="element-504" />
                      <span data-id="element-505">SUBMIT</span>
                    </>}
                </AnimatedButton>
                {/* Security notice */}
                <div className="mt-6 text-xs text-[#8a9cb9] font-mono flex items-start gap-2" data-id="element-506">
                  <AlertCircleIcon size={14} className="mt-0.5 text-[#5ee7df]" data-id="element-507" />
                  <span data-id="element-508">
                    Transmissions encrypted with quantum-resistant protocols.
                  </span>
                </div>
              </form>}
          </div>
        </div>
      </div>
    </section>;
};